import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import {
  fetchDepartments,
  deleteDepartment,
  editDepartment,
  Department,
  fetchCompanyDepartments
} from 'store/departmentmanagementSlice';
import { fetchCompanies } from 'store/companySlice';
import { fetchUsers } from 'store/userSlice';
import EditDepartmentModal from 'components/modals/EditDepartmentModal';
import DeleteDepartmentModal from 'components/modals/DeleteDepartmentModal';
import ViewDepartmentModal from 'components/modals/ViewDepartmentModal';
import PhoenixLoader from 'components/common/PhoenixLoader';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSitemap,
  faBuilding,
  faCalendar,
  faCog
} from '@fortawesome/free-solid-svg-icons';

interface DeptTableProps {
  searchQuery?: string;
  selectedCompanyId?: number | null;
}

const DepartmentTable = ({
  searchQuery = '',
  selectedCompanyId
}: DeptTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const userRole = useSelector((state: RootState) => state.auth.role);
  const userCompanyId = useSelector((state: RootState) => state.auth.companyId);

  const {
    data: departments,
    loading,
    error
  } = useSelector((state: RootState) => state.department);

  const { data: companies } = useSelector(
    (state: RootState) => state.companies
  );
  const { data: users } = useSelector((state: RootState) => state.users);

  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    // For super admin: fetch all departments or specific company departments
    if (userRole === 'super_user') {
      if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
        dispatch(fetchCompanyDepartments(selectedCompanyId));
      } else {
        dispatch(fetchDepartments()); // Fetch ALL departments
      }
    } else {
      // For company admin/user: fetch only their company's departments
      const userCompany = parseInt(userCompanyId || '1');
      dispatch(fetchCompanyDepartments(userCompany));
    }

    dispatch(fetchCompanies());
    dispatch(fetchUsers());
  }, [dispatch, selectedCompanyId, userRole, userCompanyId]);

  // Helper functions
  const getCompanyName = (companyId: number): string => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || `Company #${companyId}`;
  };

  const getUserName = (userId: number): string => {
    const user = users.find(u => u.id === userId);
    return user?.name || `User #${userId}`;
  };

  const filtered = useMemo(() => {
    let result = departments;

    // Filter by search query
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      result = result.filter(
        d =>
          d.name.toLowerCase().includes(q) ||
          getCompanyName(d.company_id).toLowerCase().includes(q) ||
          getUserName(d.head_user_id).toLowerCase().includes(q)
      );
    }

    // For super admin: show all departments unless specific company is selected
    if (userRole === 'super_user') {
      if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
        result = result.filter(d => d.company_id === selectedCompanyId);
      }
      // If selectedCompanyId is null, show all departments (no additional filtering)
    } else {
      // For company admin/user: only show their company's departments
      const userCompany = parseInt(userCompanyId || '1');
      result = result.filter(d => d.company_id === userCompany);
    }

    return result;
  }, [
    departments,
    searchQuery,
    selectedCompanyId,
    companies,
    users,
    userRole,
    userCompanyId
  ]);

  // Event handlers
  const handleEdit = (dept: Department) => {
    setSelectedDept(dept);
    setShowEditModal(true);
  };

  const handleView = (dept: Department) => {
    setSelectedDept(dept);
    setShowViewModal(true);
  };

  const handleDelete = (dept: Department) => {
    setSelectedDept(dept);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedDept) {
      await dispatch(deleteDepartment(selectedDept.id));
    }
    setShowDeleteModal(false);
    setSelectedDept(null);
  };

  const saveEdit = async (updatedData: {
    id: number;
    name: string;
    head_user_id: number | null;
    company_id: number;
  }) => {
    await dispatch(
      editDepartment({
        id: updatedData.id,
        departmentData: {
          name: updatedData.name,
          head_user_id:
            updatedData.head_user_id === null
              ? undefined
              : updatedData.head_user_id
        }
      })
    );
    setShowEditModal(false);
    setSelectedDept(null);
  };

  // Column definitions
  const cols = useMemo<ColumnDef<Department>[]>(
    () => [
      {
        id: 'sn',
        header: 'SN',
        cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
        meta: {
          headerProps: {
            style: { width: '80px' }
          },
          cellProps: {}
        }
      },
      {
        accessorKey: 'name',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faSitemap} className="me-2 text-primary" />
            Department Name
          </span>
        ),
        cell: ({ row: { original } }) => (
          <button
            className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
            onClick={() => handleView(original)}
          >
            {original.name}
          </button>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        id: 'company',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
            Company
          </span>
        ),
        cell: ({ row: { original } }) => (
          <span className="text-body">
            {getCompanyName(original.company_id)}
          </span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      // {
      //   id: 'head_user',
      //   header: 'Department Head',
      //   cell: ({ row: { original } }) => (
      //     <span className="text-body">
      //       {original.head_user_id === 0 || !original.head_user_id
      //         ? '-'
      //         : getUserName(original.head_user_id)}
      //     </span>
      //   ),
      //   meta: {
      //     headerProps: { className: 'ps-4 bg-white py-3 text-uppercase' },
      //     cellProps: { className: 'white-space-nowrap ps-4' }
      //   }
      // },
      {
        id: 'created_at',
        header: () => (
          <span>
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
            Created Date
          </span>
        ),
        cell: ({ row: { original } }) => (
          <span className="text-body">
            {new Date(original.created_at).toLocaleDateString()}
          </span>
        ),
        meta: {
          headerProps: {},
          cellProps: {}
        }
      },
      {
        id: 'actions',
        header: () => (
          <div className="d-inline-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
            Actions
          </div>
        ),
        cell: ({ row: { original } }) => (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems
                onView={() => handleView(original)}
                onEdit={() => handleEdit(original)}
                onRemove={() => handleDelete(original)}
              />
            </RevealDropdown>
          </RevealDropdownTrigger>
        ),
        meta: {
          headerProps: {
            className: 'text-end pe-3'
          },
          cellProps: { className: 'text-end pe-3' }
        }
      }
    ],
    [companies, users]
  );

  const table = useAdvanceTable({
    data: filtered,
    columns: cols,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {loading ? (
          <PhoenixLoader />
        ) : error ? (
          <p className="p-4 text-danger">{error}</p>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedDept && (
        <ViewDepartmentModal
          show={showViewModal}
          handleClose={() => {
            setShowViewModal(false);
            setSelectedDept(null);
          }}
          dept={selectedDept}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDept && (
        <EditDepartmentModal
          show={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedDept(null);
          }}
          dept={selectedDept}
          onSave={saveEdit}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedDept && (
        <DeleteDepartmentModal
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setSelectedDept(null);
          }}
          dept={selectedDept}
          onDeleted={confirmDelete}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default DepartmentTable;
